import { useState, useCallback } from 'react';
import { createApiHeaders, apiFetch } from '../../../../config/api';
import {
  MASTER_TEMPLATE_ID,
  REQUEST_TIMEOUT_LLM_EXECUTION,
} from '../../../../config/constants';

/**
 * useTemplateGeneration Hook
 * Handles AI template generation
 */
export const useTemplateGeneration = (setFormData, setError, setSuccess) => {
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatingTemplate, setGeneratingTemplate] = useState(false);

  const handleGenerateTemplate = useCallback(
    async formData => {
      if (!aiPrompt.trim()) {
        setError(
          'Please enter a description for the AI to generate a template.'
        );
        return;
      }

      setGeneratingTemplate(true);
      setError(null);
      setSuccess(null);

      if (!MASTER_TEMPLATE_ID || MASTER_TEMPLATE_ID === '') {
        setError(
          'AI Template Generator is not configured. Please contact your administrator.'
        );
        setGeneratingTemplate(false);
        return;
      }

      // Validate that provider and model are configured
      if (!formData.model_config?.provider) {
        setError(
          'Please configure a provider in the Model Configuration section before using the AI Template Generator.'
        );
        setGeneratingTemplate(false);
        return;
      }

      if (!formData.model_config?.model_name) {
        setError(
          'Please configure a model in the Model Configuration section before using the AI Template Generator.'
        );
        setGeneratingTemplate(false);
        return;
      }

      try {
        const headers = createApiHeaders();

        const payload = {
          input_data: {
            user_requirement: aiPrompt,
            category: formData.category,
            output_format: formData.output_format,
            model_name: formData.model_config.model_name,
            temperature: formData.model_config.temperature,
            max_tokens: formData.model_config.max_tokens,
          },
          llm_config: {
            provider: formData.model_config.provider,
            model_name: formData.model_config.model_name,
            temperature: formData.model_config.temperature,
            max_tokens: formData.model_config.max_tokens,
            top_p: formData.model_config.top_p,
            frequency_penalty: formData.model_config.frequency_penalty,
            presence_penalty: formData.model_config.presence_penalty,
          },
        };

        const requestPayload = { ...payload, allow_draft: true };

        // Use apiFetch with 300 second timeout for template execution
        const result = await apiFetch(
          `/api/v1/templates/executions/${MASTER_TEMPLATE_ID}/execute`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(requestPayload),
            timeout: REQUEST_TIMEOUT_LLM_EXECUTION, // 300 seconds for template execution
          }
        );

        const generatedTemplate =
          result.raw_response ||
          result.output_data?.template ||
          result.formatted_prompt;

        if (generatedTemplate) {
          setFormData(prev => ({
            ...prev,
            template: generatedTemplate,
          }));
          setSuccess(
            'Template generated successfully! You can now review and edit it.'
          );
          setAiPrompt('');
        } else {
          throw new Error('No template content received from the generator');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setGeneratingTemplate(false);
      }
    },
    [aiPrompt, setFormData, setError, setSuccess]
  );

  return {
    aiPrompt,
    generatingTemplate,
    setAiPrompt,
    handleGenerateTemplate,
  };
};
